import classNames from 'classnames';
import React, {
    useState,
    ReactNode,
    Children,
    isValidElement,
    cloneElement,
    ReactElement,
    CSSProperties,
    FC,
} from 'react';
import ClickAwayListener from 'react-click-away-listener';
import styled, { Interpolation, useTheme } from 'styled-components';

import { DropDownArrow } from '../icons';

interface SelectProps {
    value: any;
    name?: string;
    // eslint-disable-next-line no-unused-vars
    onChange: (value: any, name: string | undefined) => void;
    children: ReactNode;
    className?: string;
    arrow?: boolean;
    classes?: {
        root?: string;
        selected?: string;
    };
    styles?: Interpolation<CSSProperties>;
    headStyles?: Interpolation<CSSProperties>;
    disabled?: boolean;
    fullWidth?: boolean;
}

const StyledSelect = styled.div<{
    styles: Interpolation<CSSProperties>;
}>`
    width: 'auto';
    display: inline-block;
    position: relative;
    &.select-fullWidth {
        width: '100%';
    }
    ${({ styles }) => styles};
`;

const StyledSelectedValue = styled.div<{
    styles: Interpolation<CSSProperties>;
}>`
    ${({ theme: { colors, typography } }) => `
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px;
        cursor: pointer;
        border-radius: 4px;
        border: 1px solid ${colors.border.light};
        color: ${colors.text.primary};
        &.selected-disabled {
            cursor: default;
            color: ${colors.text.disabled};
        }
        &:hover:not(.selected-disabled){
            border-color: ${colors.primary};
        }
        & > span {
            display: flex;
            align-items: center;
            font-size: ${typography.size.desktop.paragraph.p3};
            line-height: ${typography.lineHeight[6]};
            margin-right: 8px;
        }
    `}
    ${({ styles }) => styles};
`;

const StyledOptions = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    border-radius: 4px;
    max-height: 200px;
    overflow-y: auto;
    z-index: 1300;
    ${({ theme: { colors } }) => `
        background-color: ${colors.background.secondary.v3};
        border: 1px solid ${colors.border.light};
    `}
`;

const Select: FC<SelectProps> = ({
    value,
    name,
    onChange,
    children,
    className,
    arrow = true,
    classes,
    styles,
    headStyles,
    disabled,
    fullWidth,
}) => {
    const { colors } = useTheme();
    const [open, setOpen] = useState(false);
    const selectedChild = Children.toArray(children).find((child) => {
        if (isValidElement(child)) {
            return child.props.value === value;
        }
        return false;
    });
    return (
        <ClickAwayListener
            onClickAway={() => {
                setOpen(false);
            }}
        >
            <StyledSelect
                className={classNames(
                    'select',
                    fullWidth && 'select-fullWidth',
                    classes && classes.root && classes.root,
                    className,
                )}
                onClick={() => {
                    !disabled && setOpen(!open);
                }}
                styles={styles}
            >
                <StyledSelectedValue
                    className={classNames('selected', disabled && 'selected-disabled')}
                    styles={headStyles}
                >
                    <span>
                        {isValidElement(selectedChild) ? selectedChild.props.children : value}
                    </span>
                    {arrow && (
                        <DropDownArrow
                            stroke={disabled ? colors.text.disabled : colors.text.primary}
                            style={open ? { transform: 'rotate(180deg)' } : {}}
                        />
                    )}
                </StyledSelectedValue>
                {open && (
                    <StyledOptions>
                        {Children.map(children, (child) => {
                            if (isValidElement(child)) {
                                return cloneElement(child as ReactElement, {
                                    onClick: () => {
                                        onChange(child.props.value, name);
                                        setOpen(false);
                                    },
                                    className: classNames(
                                        child.props.value === value && 'selected',
                                        child.props.value === value &&
                                            classes &&
                                            classes.selected &&
                                            classes.selected,
                                    ),
                                });
                            }
                            return child;
                        })}
                    </StyledOptions>
                )}
            </StyledSelect>
        </ClickAwayListener>
    );
};

export default Select;
